import { useEffect, useRef, useState } from 'react';
import { api, ApiError, errorText } from '../../api';
import { Dialog } from '../../components/Dialog';
import { stillActive } from '../../lib/activity';
import { fmtDate, plural } from '../../lib/format';
import { pdfPages } from '../../lib/pdfjs';
import { useToast } from '../../lib/toast';
import type { ImportCheck, ImportField, ImportRead } from './types';
import { KiHinweis } from '../KiHinweis';

// Mitarbeiter übernehmen, wenn ein Mandant mit vielen Angestellten zu uns wechselt: Liste hochladen (Excel und CSV
// liest die App, PDFs und Fotos die KI), in der Vorschau prüfen und ergänzen, dann alle auf einmal anlegen. Angaben der
// KI sind gelb, bis sie geändert werden; rot markierte Mitarbeiter gehen erst nach dem Korrigieren oder Abwählen.
//
// PDFs liest die KI in Teilen, einen nach dem anderen (Sam 12.10.: ein Personalstammblatt mit 600 Seiten): Text-PDFs
// als Text (günstiger), gescannte Seiten als Datei mit Seitenbereich. Die Mitarbeiter kommen nach und nach in die
// Vorschau; wer über die Grenze zweier Teile geht, wird zusammengeführt. Anhalten geht jederzeit, nach einem Fehler
// geht es beim selben Teil weiter.

interface Row {
  line: number;
  values: Record<string, string>;
  // nicht erkannte Angaben, so wie sie in der Liste stehen
  raw: Record<string, string>;
  // von der KI gelesen (gelb), bis geändert
  ki: Set<string>;
  on: boolean;
  check: ImportCheck;
  // PDF in Teilen: auf welchen Seiten der Mitarbeiter steht
  seiten?: Part;
}

// Je Teil höchstens so viel Text und so viele Seiten (der KI-Anbieter begrenzt, wie viel die KI je Minute bekommt);
// gescannte Seiten je Teil (Server: KI_TEIL_SEITEN, höchstens 10)
const TEIL_ZEICHEN = 30_000;
const TEIL_SEITEN_TEXT = 20;
const TEIL_SEITEN_SCAN = 8;
// „zu viel auf einmal“ (Grenze je Minute): warten und denselben Teil noch einmal, höchstens so oft hintereinander.
// Klappt es nach einer Pause wieder nicht, ist schon der Teil allein zu groß für die Grenze: dann halbieren.
const PAUSE_S = 60;
const PAUSEN = 8;

interface Part {
  von: number;
  bis: number;
}

interface Job {
  id: number;
  file: File;
  // Text je Seite (Text-PDF); null: gescannt oder im Browser nicht lesbar, der Server schneidet die Seiten aus der Datei
  pages: string[] | null;
  // der Browser konnte die PDF nicht öffnen (die Seiten hat der Server gezählt)
  noBrowser: boolean;
  seiten: number;
  parts: Part[];
  // nächster Teil
  next: number;
  state: 'plan' | 'run' | 'stop' | 'error' | 'done';
  // Sekunden bis zum nächsten Versuch, wenn die KI eine Pause braucht
  wait: number | null;
  // übersprungene Teile und Hinweise der KI zu einzelnen Teilen
  skipped: Part[];
  notes: string[];
}

// PDF am Anfang der Datei erkennen (falls Name und Art fehlen, z. B. ohne Endung gespeichert)
const isPdf = async (f: File) => new TextDecoder().decode(await f.slice(0, 5).arrayBuffer()) === '%PDF-';

const pagesText = (p: Part) => (p.von === p.bis ? `Seite ${p.von}` : `Seiten ${p.von}–${p.bis}`);
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Text-PDF, wenn fast alle Seiten Text haben (gescannte Seiten haben keinen)
const textual = (pages: string[]) =>
  pages.filter((t) => t.replace(/\s/g, '').length >= 30).length >= Math.ceil(pages.length * 0.8);

function plan(pages: string[] | null, count: number): Part[] {
  const parts: Part[] = [];
  let von = 1;
  let size = 0;
  for (let p = 1; p <= count; p++) {
    const len = pages ? pages[p - 1]!.length + 20 : 0;
    const n = p - von;
    if (n > 0 && (pages ? size + len > TEIL_ZEICHEN || n >= TEIL_SEITEN_TEXT : n >= TEIL_SEITEN_SCAN)) {
      parts.push({ von, bis: p - 1 });
      von = p;
      size = 0;
    }
    size += len;
  }
  parts.push({ von, bis: count });
  return parts;
}

const partText = (pages: string[], p: Part) =>
  pages
    .slice(p.von - 1, p.bis)
    .map((t, i) => `--- Seite ${p.von + i} ---\n${t}`)
    .join('\n')
    .slice(0, 60_000);

// Derselbe Mitarbeiter in zwei Teilen: gleiche Steuer-ID oder SV-Nummer, sonst gleicher Name ohne widersprüchliche
// Angaben (anderes Geburtsdatum: jemand anderes)
const low = (r: Row, k: string) => r.values[k]?.trim().toLocaleLowerCase('de') ?? '';
function same(a: Row, b: Row): boolean {
  if (['steuerId', 'svNummer'].some((k) => low(a, k) && low(a, k) === low(b, k))) return true;
  if (!low(a, 'nachname') || low(a, 'nachname') !== low(b, 'nachname') || low(a, 'vorname') !== low(b, 'vorname')) return false;
  return !['geburtsdatum', 'steuerId', 'svNummer'].some((k) => low(a, k) && low(b, k) && low(a, k) !== low(b, k));
}

// Neue Zeilen dazu: Bekannte ergänzen (was fehlt, gelb), Neue hinten an, über der Höchstzahl nicht
function merge(cur: Row[], add: Row[], max: number): { rows: Row[]; over: number } {
  const rows = [...cur];
  let over = 0;
  for (const r of add) {
    const i = rows.findIndex((o) => same(o, r));
    if (i < 0) {
      if (rows.length >= max) over++;
      else rows.push({ ...r, line: rows.length + 1 });
      continue;
    }
    const o = rows[i]!;
    const values = { ...o.values };
    const raw = { ...o.raw };
    const ki = new Set(o.ki);
    for (const [k, v] of Object.entries(r.values)) {
      if (values[k]?.trim()) continue;
      values[k] = v;
      ki.add(k);
      delete raw[k];
    }
    for (const [k, v] of Object.entries(r.raw)) if (!values[k]?.trim() && !raw[k]) raw[k] = v;
    const seiten = o.seiten &&
      r.seiten && { von: Math.min(o.seiten.von, r.seiten.von), bis: Math.max(o.seiten.bis, r.seiten.bis) };
    rows[i] = { ...o, values, raw, ki, seiten: seiten || o.seiten };
  }
  return { rows, over };
}

const SOURCE: Record<ImportRead['source'], string> = {
  excel: 'aus der Excel-Datei',
  csv: 'aus der CSV-Datei',
  ki: 'von der KI gelesen (gelb). Bitte mit der Datei vergleichen',
};
const ISO = /^\d{4}-\d{2}-\d{2}$/;

const shown = (v: string | number) => (typeof v === 'number' ? String(v).replace('.', ',') : v);

// „2.800,50“ → 2800.5; was keine Zahl ist, geht so an den Server (der meldet es)
function toNumber(s: string): number | string {
  const t = s.replace(/\s|€/g, '');
  const n = Number(t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t);
  return t && Number.isFinite(n) ? n : s;
}

function toRows(r: ImportRead, seiten?: Part): Row[] {
  return r.rows.map((x) => ({
    line: x.line,
    values: Object.fromEntries(Object.entries(x.values).map(([k, v]) => [k, shown(v)])),
    raw: x.raw,
    ki: new Set(r.source === 'ki' ? Object.keys(x.values) : []),
    on: !x.dup,
    check: { errors: x.errors, warnings: x.warnings, dup: x.dup, nachtragen: x.nachtragen },
    seiten,
  }));
}

function payload(r: Row, fields: ImportField[]) {
  const out: Record<string, string | number> = {};
  for (const f of fields) {
    const v = r.values[f.key]?.trim();
    if (v) out[f.key] = f.kind === 'num' ? toNumber(v) : v;
  }
  return out;
}

export function ImportDialog({
  clientId,
  contactName,
  ki,
  kiTest,
  kiAus,
  onClose,
  onDone,
}: {
  clientId: string;
  contactName: string;
  ki: boolean;
  kiTest: boolean;
  kiAus: boolean;
  onClose(): void;
  onDone(): void;
}) {
  const toast = useToast();
  const [read, setRead] = useState<ImportRead | null>(null);
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState<'read' | 'save' | null>(null);
  const [viaKi, setViaKi] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<number | null>(null);
  const [over, setOver] = useState(false);
  // nach jeder Änderung neu prüfen (kurz gewartet, damit nicht bei jedem Tastendruck)
  const [version, setVersion] = useState(0);
  const [checking, setChecking] = useState(false);
  const pick = useRef<HTMLInputElement>(null);
  const run = useRef(0);
  // PDF in Teilen: der Stand auch im Ref, weil die Schleife über viele Anfragen läuft
  const [job, setJobState] = useState<Job | null>(null);
  const jobRef = useRef<Job | null>(null);
  const jobs = useRef(0);
  const halt = useRef(false);
  const [halting, setHalting] = useState(false);
  // Text der PDF wird gelesen (Seite x von y)
  const [prep, setPrep] = useState<string | null>(null);
  // Mitarbeiter über der Höchstzahl, je Teil (im Updater gesetzt, deshalb je Teil und nicht aufaddiert)
  const dropped = useRef(new Map<string, number>());

  const setJob = (j: Job | null) => {
    jobRef.current = j;
    setJobState(j);
  };
  const patchJob = (id: number, p: Partial<Job>) => {
    const j = jobRef.current;
    if (j?.id === id) setJob({ ...j, ...p });
  };
  // Dialog zu: eine laufende Schleife hört auf
  useEffect(
    () => () => {
      halt.current = true;
      jobRef.current = null;
    },
    [],
  );

  const reset = () => {
    halt.current = true;
    setJob(null);
    setHalting(false);
    dropped.current.clear();
    setRead(null);
    setRows([]);
    setError(null);
  };

  const readFile = async (f: File) => {
    setError(null);
    setBusy('read');
    setViaKi(/^(application\/pdf|image\/)/.test(f.type) || /\.(pdf|jpe?g|png|webp)$/i.test(f.name));
    if (ki && (f.type === 'application/pdf' || /\.pdf$/i.test(f.name) || (await isPdf(f)))) {
      let pages: string[] | null = null;
      let seiten = 0;
      try {
        pages = await pdfPages(await f.arrayBuffer(), (n, all) => setPrep(`Seite ${n} von ${all}`));
        seiten = pages.length;
      } catch (err) {
        // pdf.js kann sie hier nicht öffnen: Die Seiten zählt der Server, gelesen wird trotzdem in Teilen (eine lange PDF
        // auf einmal wäre zu viel für die KI). Der Grund kommt ins Protokoll des Servers, ohne Inhalte der Datei.
        seiten = await serverPages(f, err);
      } finally {
        setPrep(null);
      }
      if (seiten) {
        setBusy(null);
        const text = !!pages && textual(pages);
        const parts = plan(text ? pages : null, seiten);
        dropped.current.clear();
        const j: Job = {
          id: ++jobs.current,
          file: f,
          pages: text ? pages : null,
          noBrowser: !pages,
          seiten,
          parts,
          next: 0,
          state: 'plan',
          wait: null,
          skipped: [],
          notes: [],
        };
        setJob(j);
        if (parts.length === 1) void runJob(j.id);
        return;
      }
    }
    const fd = new FormData();
    fd.append('file', f, f.name);
    try {
      const r = await api.post<ImportRead>(`/api/kanzlei/lohn/${clientId}/import/lesen`, fd);
      setRead(r);
      setRows(toRows(r));
      setOpen(null);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  // Seitenzahl vom Server, wenn der Browser die PDF nicht öffnen kann (0: auch der Server nicht)
  const serverPages = async (f: File, err: unknown): Promise<number> => {
    const fd = new FormData();
    fd.append('grund', (err instanceof Error ? `${err.name}: ${err.message}` : String(err)).slice(0, 200));
    fd.append('file', f, f.name);
    try {
      return (await api.post<{ seiten: number }>(`/api/kanzlei/lohn/${clientId}/import/seiten`, fd)).seiten;
    } catch {
      return 0;
    }
  };

  const readPart = (j: Job, p: Part) => {
    if (j.pages) {
      return api.post<ImportRead>(`/api/kanzlei/lohn/${clientId}/import/lesen-text`, {
        text: partText(j.pages, p),
        von: p.von,
        bis: p.bis,
        seiten: j.seiten,
      });
    }
    const fd = new FormData();
    fd.append('file', j.file, j.file.name);
    return api.post<ImportRead>(`/api/kanzlei/lohn/${clientId}/import/lesen?von=${p.von}&bis=${p.bis}`, fd);
  };

  // Teil für Teil, bis alle gelesen sind, jemand anhält oder ein Fehler kommt
  const runJob = async (id: number) => {
    halt.current = false;
    setHalting(false);
    setError(null);
    patchJob(id, { state: 'run', wait: null });
    let pauses = 0;
    for (;;) {
      const j = jobRef.current;
      if (!j || j.id !== id) return;
      if (j.next >= j.parts.length) break;
      if (halt.current) {
        setHalting(false);
        patchJob(id, { state: 'stop', wait: null });
        return;
      }
      const part = j.parts[j.next]!;
      const whole = j.parts.length === 1;
      try {
        const r = await readPart(j, part);
        const cur = jobRef.current;
        if (cur?.id !== id) return;
        pauses = 0;
        const key = `${part.von}-${part.bis}`;
        setRead((prev) =>
          prev ? { ...prev, skipped: prev.skipped + r.skipped } : { ...r, rows: [], hinweis: whole ? r.hinweis : null },
        );
        setRows((rows) => {
          const m = merge(rows, toRows(r, whole ? undefined : part), r.max);
          dropped.current.set(key, m.over);
          return m.rows;
        });
        setVersion((v) => v + 1);
        patchJob(id, {
          next: cur.next + 1,
          notes: r.hinweis && !whole ? [...cur.notes, `${pagesText(part)}: ${r.hinweis}`] : cur.notes,
        });
        stillActive();
      } catch (err) {
        const cur = jobRef.current;
        if (cur?.id !== id) return;
        const code = err instanceof ApiError ? err.code : undefined;
        const halve = () => {
          const mid = Math.floor((part.von + part.bis) / 2);
          const parts = [...cur.parts];
          parts.splice(cur.next, 1, { von: part.von, bis: mid }, { von: mid + 1, bis: part.bis });
          patchJob(id, { parts });
        };
        // Antwort zu lang: den Teil halbieren
        if (code === 'ki_too_long' && part.bis > part.von) {
          halve();
          continue;
        }
        if (code === 'ki_busy' && pauses < PAUSEN) {
          pauses++;
          if (pauses > 1 && part.bis > part.von) halve();
          for (let s = PAUSE_S; s > 0 && !halt.current && jobRef.current?.id === id; s--) {
            patchJob(id, { wait: s });
            await sleep(1000);
          }
          patchJob(id, { wait: null });
          stillActive();
          continue;
        }
        setError(errorText(err));
        // die ganze Datei in einem Teil: wie beim Hochladen ohne Teile, nur die Meldung
        if (whole) setJob(null);
        else patchJob(id, { state: 'error', wait: null });
        return;
      }
    }
    patchJob(id, { state: 'done' });
    setOpen(null);
  };

  const stop = () => {
    halt.current = true;
    setHalting(true);
  };
  const skip = () => {
    const j = jobRef.current;
    if (!j) return;
    setJob({ ...j, next: j.next + 1, skipped: [...j.skipped, j.parts[j.next]!] });
    void runJob(j.id);
  };

  useEffect(() => {
    if (!version || !read) return;
    const id = ++run.current;
    setChecking(true);
    const on = rows.flatMap((r, i) => (r.on ? [i] : []));
    const timer = setTimeout(async () => {
      try {
        if (!on.length) return;
        const res = await api.post<{ rows: ImportCheck[] }>(`/api/kanzlei/lohn/${clientId}/import/pruefen`, {
          employees: on.map((i) => payload(rows[i]!, read.fields)),
        });
        if (id !== run.current) return;
        setRows((cur) =>
          cur.map((r, i) => {
            const k = on.indexOf(i);
            return k >= 0 ? { ...r, check: res.rows[k]! } : r;
          }),
        );
        setError(null);
      } catch (err) {
        if (id === run.current) setError(errorText(err));
      } finally {
        if (id === run.current) setChecking(false);
      }
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version]);

  const change = (fn: (r: Row) => Row, which?: number) => {
    setRows((cur) => cur.map((r, i) => (which === undefined || i === which ? fn(r) : r)));
    setVersion((v) => v + 1);
  };
  const edit = (i: number, key: string, value: string) =>
    change((r) => {
      const k = new Set(r.ki);
      k.delete(key);
      return { ...r, values: { ...r.values, [key]: value }, ki: k };
    }, i);

  const save = async () => {
    if (!read) return;
    setBusy('save');
    setError(null);
    try {
      const r = await api.post<{ created: number; nachgetragen: number }>(`/api/kanzlei/lohn/${clientId}/import`, {
        employees: rows.filter((x) => x.on).map((x) => payload(x, read.fields)),
      });
      toast(
        [
          r.created && `${r.created} Mitarbeiter übernommen · ${contactName} sieht sie in der App`,
          r.nachgetragen && `Austritt bei ${plural(r.nachgetragen, 'Mitarbeiter', 'Mitarbeitern')} nachgetragen`,
        ]
          .filter(Boolean)
          .join(' · '),
      );
      onDone();
      onClose();
    } catch (err) {
      setError(errorText(err));
      setVersion((v) => v + 1);
    } finally {
      setBusy(null);
    }
  };

  // Stand beim Lesen in Teilen: Plan zum Starten, Fortschritt, Anhalten, nach einem Fehler weiter
  const progress = (j: Job) => {
    const part = j.parts[j.next];
    const sofar = plural(rows.length, 'Mitarbeiter', 'Mitarbeiter');
    if (j.state === 'plan') {
      return (
        <div className="imp-prog">
          <p className="note">
            „{j.file.name}“ hat {j.seiten} Seiten. Die KI liest sie in {j.parts.length} Teilen nacheinander, die Mitarbeiter
            kommen nach und nach in die Vorschau. Das dauert etwa eine Minute je Teil, dieses Fenster bitte so lange offen lassen.
            Anhalten geht jederzeit.
          </p>
          {!j.pages && (
            <p className="note">
              {j.noBrowser
                ? 'Der Browser konnte die PDF nicht öffnen, die KI liest die Seiten als Bilder (dauert länger).'
                : 'Die Seiten sind gescannt, die KI liest sie als Bilder (dauert länger).'}
            </p>
          )}
          <div className="imp-prog-btns">
            <button type="button" className="btn dark" onClick={() => void runJob(j.id)}>
              Lesen starten
            </button>
            <button type="button" className="btn" onClick={reset}>
              Andere Datei
            </button>
          </div>
        </div>
      );
    }
    const wait = j.wait !== null && (
      <p className="note imp-warn">
        Die KI braucht eine kurze Pause (zu viel auf einmal). Es geht in {j.wait} Sekunden von selbst weiter.
      </p>
    );
    if (j.parts.length === 1) {
      return (
        <>
          <p className="note imp-busy" role="status">
            <span className="spinner sm" aria-hidden="true" /> Die KI liest die Liste. Bei vielen Mitarbeitern dauert das ein paar
            Minuten …
          </p>
          {wait}
        </>
      );
    }
    return (
      <div className="imp-prog" role="status">
        <progress max={j.parts.length} value={j.next} aria-label="Gelesene Teile" />
        <p className="note">
          {j.state === 'run' &&
            part &&
            `Teil ${j.next + 1} von ${j.parts.length} wird gelesen (${pagesText(part)}), bisher ${sofar}.`}
          {j.state === 'stop' && `Angehalten nach ${j.next} von ${j.parts.length} Teilen, bisher ${sofar}.`}
          {j.state === 'error' && part && `Teil ${j.next + 1} von ${j.parts.length} (${pagesText(part)}) ging nicht.`}
        </p>
        {wait}
        {halting && <p className="note">Hält nach diesem Teil an …</p>}
        <div className="imp-prog-btns">
          {j.state === 'run' && (
            <button type="button" className="btn sm" onClick={stop} disabled={halting}>
              Anhalten
            </button>
          )}
          {j.state === 'stop' && (
            <button type="button" className="btn sm dark" onClick={() => void runJob(j.id)}>
              Weiter lesen
            </button>
          )}
          {j.state === 'error' && (
            <>
              <button type="button" className="btn sm dark" onClick={() => void runJob(j.id)}>
                Noch einmal versuchen
              </button>
              <button type="button" className="btn sm" onClick={skip}>
                Teil überspringen
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (!read) {
    return (
      <Dialog open onClose={onClose} title="Mitarbeiter übernehmen" className="imp">
        <div className="imp-pick">
          <p className="note">
            Für Mandanten, die mit ihren Mitarbeitern zu uns wechseln: Liste hochladen, prüfen und alle auf einmal übernehmen.
            Excel und CSV liest die App direkt{ki ? ', PDFs und Fotos liest die KI' : ''}. Gespeichert wird erst beim Übernehmen.
          </p>
          <p className="note">
            <a href="/api/kanzlei/lohn/import/vorlage" download>
              Excel-Vorlage herunterladen
            </a>{' '}
            und dem Mandanten zum Ausfüllen schicken.
          </p>
          {ki && (
            <KiHinweis test={kiTest}>
              PDFs und Fotos liest die KI (über Google Cloud, Rechenzentren in der EU). Konfession und Schwerbehinderung
              liest sie nicht aus, die tragen Sie selbst ein.
            </KiHinweis>
          )}
          {!ki && (
            <p className="note">
              {kiAus
                ? 'Die KI ist für diesen Mandanten ausgeschaltet: Bitte die Liste als Excel oder CSV hochladen.'
                : 'PDFs und Fotos gehen, sobald die KI eingerichtet ist.'}
            </p>
          )}
          {job && job.state !== 'done' ? (
            progress(job)
          ) : busy === 'read' ? (
            <p className="note imp-busy" role="status">
              <span className="spinner sm" aria-hidden="true" />{' '}
              {prep
                ? `Die PDF wird vorbereitet (${prep}) …`
                : viaKi
                  ? 'Die KI liest die Liste. Bei vielen Mitarbeitern dauert das ein paar Minuten …'
                  : 'Die Liste wird gelesen …'}
            </p>
          ) : (
            <button
              type="button"
              className={`drop${over ? ' over' : ''}`}
              onClick={() => pick.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const f = e.dataTransfer.files[0];
                if (f) void readFile(f);
              }}
            >
              <b>Liste hierher ziehen oder klicken</b>
              <small>Excel (.xlsx) oder CSV{ki ? ', PDF oder Foto' : ''}</small>
            </button>
          )}
          <input
            ref={pick}
            type="file"
            hidden
            accept=".xlsx,.csv,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/pdf,image/*"
            data-testid="import-pick"
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void readFile(f);
            }}
          />
          {error && (
            <p className="note imp-err" role="alert">
              {error}
            </p>
          )}
          <div className="dlg-actions">
            <button type="button" className="btn" onClick={onClose}>
              Abbrechen
            </button>
          </div>
        </div>
      </Dialog>
    );
  }

  const chosen = rows.filter((r) => r.on);
  const bad = chosen.filter((r) => r.check.errors.length).length;
  // schon in der App: nur der Austritt kommt dazu
  const nach = chosen.filter((r) => r.check.nachtragen).length;
  const neu = chosen.length - nach;
  const left = chosen.some((r) => !r.check.nachtragen && r.values.austritt);
  const skipped = read.skipped + [...dropped.current.values()].reduce((a, b) => a + b, 0);
  const reading = job?.state === 'run' || job?.state === 'plan';
  return (
    <Dialog open onClose={onClose} title="Mitarbeiter übernehmen" className="imp">
      <p className="note">
        {plural(rows.length, 'Mitarbeiter', 'Mitarbeiter')} {SOURCE[read.source]}.{read.hinweis && ` ${read.hinweis}`}
      </p>
      {read.ignored.length > 0 && <p className="note">Nicht übernommen (Spalten ohne Feld): {read.ignored.join(', ')}</p>}
      {skipped > 0 && (
        <p className="note imp-warn">
          {plural(skipped, 'Zeile', 'Zeilen')} ohne Namen oder über {read.max} Mitarbeitern nicht übernommen.
        </p>
      )}
      {job && job.state !== 'done' && progress(job)}
      {job?.state === 'done' && job.parts.length > 1 && <p className="note">Alle {job.seiten} Seiten gelesen.</p>}
      {job && job.skipped.length > 0 && (
        <p className="note imp-warn">
          Nicht gelesen: {job.skipped.map(pagesText).join(', ')}. Diese Seiten bitte selbst ansehen.
        </p>
      )}
      {job && job.notes.length > 0 && (
        <ul className="note imp-notes">
          {job.notes.slice(0, 8).map((n) => (
            <li key={n}>{n}</li>
          ))}
          {job.notes.length > 8 && <li>und {plural(job.notes.length - 8, 'weiterer Hinweis', 'weitere Hinweise')}</li>}
        </ul>
      )}
      <div className="imp-all">
        <label>
          <input
            type="checkbox"
            checked={chosen.length === rows.length}
            onChange={(e) => change((r) => ({ ...r, on: e.target.checked }))}
          />{' '}
          Alle auswählen
        </label>
        <span>
          {chosen.length} ausgewählt{checking ? ' · wird geprüft …' : ''}
        </span>
      </div>
      <div className="imp-list">
        {rows.map((r, i) => {
          const name = `${r.values.vorname ?? ''} ${r.values.nachname ?? ''}`.trim() || 'Ohne Namen';
          const unread = read.fields.filter((f) => r.raw[f.key] && !r.values[f.key]?.trim()).length;
          const state = !r.on ? 'off' : r.check.errors.length ? 'bad' : r.check.warnings.length || unread ? 'warn' : 'ok';
          const entry = r.values.eintritt && ISO.test(r.values.eintritt) ? `Eintritt ${fmtDate(r.values.eintritt)}` : null;
          const exit = r.values.austritt && ISO.test(r.values.austritt) ? `Austritt ${fmtDate(r.values.austritt)}` : null;
          return (
            <div key={`${r.line}-${i}`} className={`imp-row ${state}`} data-testid="import-row">
              <div className="imp-top">
                <input
                  type="checkbox"
                  checked={r.on}
                  onChange={() => change((x) => ({ ...x, on: !x.on }), i)}
                  aria-label={`${name} übernehmen`}
                />
                <div className="tx">
                  <b>{name}</b>{' '}
                  <span className="sub">
                    {r.seiten ? pagesText(r.seiten) : read.source === 'ki' ? `Nr. ${r.line}` : `Zeile ${r.line}`}
                  </span>
                  <small>{[r.values.taetigkeit, r.values.art, entry, exit].filter(Boolean).join(' · ') || ' '}</small>
                  {r.on &&
                    r.check.errors.map((e) => (
                      <small key={e} className="imp-e">
                        ✗ {e}
                      </small>
                    ))}
                  {r.on &&
                    r.check.warnings.map((w) => (
                      <small key={w} className="imp-w">
                        ! {w}
                      </small>
                    ))}
                  {unread > 0 && (
                    <small className="imp-w">
                      ! {plural(unread, 'Angabe', 'Angaben')} nicht erkannt, bitte unter „Bearbeiten“ ansehen
                    </small>
                  )}
                </div>
                <button
                  type="button"
                  className="btn sm"
                  aria-expanded={open === i}
                  onClick={() => setOpen(open === i ? null : i)}
                >
                  {open === i ? 'Fertig' : 'Bearbeiten'}
                </button>
              </div>
              {open === i && (
                <div className="form imp-ed">
                  {read.fields.map((f) => {
                    const v = r.values[f.key] ?? '';
                    const cls = r.ki.has(f.key) ? 'ki' : undefined;
                    const label = `${f.label}${f.required ? ' *' : ''}`;
                    return (
                      <label key={f.key}>
                        {label}
                        {f.values ? (
                          <select
                            value={f.values.includes(v) ? v : ''}
                            className={cls}
                            onChange={(e) => edit(i, f.key, e.target.value)}
                          >
                            <option value="">–</option>
                            {f.values.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        ) : f.kind === 'date' ? (
                          <input
                            type="date"
                            value={ISO.test(v) ? v : ''}
                            className={cls}
                            onChange={(e) => edit(i, f.key, e.target.value)}
                          />
                        ) : (
                          <input
                            type="text"
                            inputMode={f.kind === 'num' ? 'decimal' : f.kind === 'email' ? 'email' : undefined}
                            value={v}
                            className={cls}
                            onChange={(e) => edit(i, f.key, e.target.value)}
                          />
                        )}
                        {r.raw[f.key] && !v.trim() && <span className="fh warn">In der Liste: {r.raw[f.key]}</span>}
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {error && (
        <p className="note imp-err" role="alert">
          {error}
        </p>
      )}
      <p className="note">
        {bad > 0
          ? `Erst ${bad === 1 ? 'den rot markierten Mitarbeiter' : `die ${bad} rot markierten Mitarbeiter`} korrigieren oder abwählen.`
          : `Die Mitarbeiter sind danach angemeldet, ${contactName} sieht sie sofort in der App.${
              left || nach
                ? ' Wer schon ausgeschieden ist, steht gleich bei den abgemeldeten, ein späterer Austritt bei „Kündigung gemeldet“.'
                : ''
            }`}
      </p>
      <div className="dlg-actions">
        <button
          type="button"
          className="btn dark"
          disabled={!chosen.length || bad > 0 || checking || busy !== null || reading}
          onClick={save}
        >
          {[neu || !nach ? `${neu} Mitarbeiter übernehmen` : '', nach ? `Austritt bei ${nach} nachtragen` : '']
            .filter(Boolean)
            .join(', ')}
        </button>
        <button type="button" className="btn" onClick={reset}>
          Andere Datei
        </button>
        <button type="button" className="btn" onClick={onClose}>
          Abbrechen
        </button>
      </div>
    </Dialog>
  );
}
